'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useRef } from 'react';
import { createGlobalStore } from '../factory/store-factory';
/**
 * React context for the global store
 */
const GlobalStoreContext = createContext(null);
/**
 * React context provider for the global store (web platform)
 */
export function GlobalStoreProvider({ children, initialState, ...storeOptions }) {
    // Per-request store creation for SSR safety using useRef pattern
    // This ensures each request gets its own store instance
    const storeRef = useRef(undefined);
    const serverStateRef = useRef(initialState);
    // Update ref when initialState changes (new page load)
    if (initialState !== serverStateRef.current) {
        serverStateRef.current = initialState;
    }
    if (!storeRef.current) {
        // Create store with selective merge logic
        const store = createGlobalStore({
            ...storeOptions,
            // Apply server state after persist rehydration completes
            onRehydrateStorage: () => {
                return (state, error) => {
                    // Call original callback if provided
                    if (storeOptions.onRehydrateStorage) {
                        const originalCallback = storeOptions.onRehydrateStorage();
                        if (originalCallback) {
                            originalCallback(state, error);
                        }
                    }
                    // Apply server state after rehydration (server state takes precedence)
                    if (!error && serverStateRef.current && Object.keys(serverStateRef.current).length > 0) {
                        const currentState = store.getState();
                        const mergedState = { ...currentState };
                        for (const key of Object.keys(serverStateRef.current)) {
                            const value = serverStateRef.current[key];
                            if (value && typeof value === 'object' && key in currentState) {
                                mergedState[key] = {
                                    ...currentState[key],
                                    ...value,
                                };
                            }
                            else if (value !== undefined) {
                                mergedState[key] = value;
                            }
                        }
                        store.setState(mergedState, true);
                    }
                };
            },
        });
        storeRef.current = store;
    }
    return (_jsx(GlobalStoreContext.Provider, { value: storeRef.current ?? null, children: children }));
}
/**
 * Get the store context value with proper error handling
 */
export function useStoreContext() {
    const store = useContext(GlobalStoreContext);
    if (!store) {
        throw new Error('useGlobalStore must be used within a GlobalStoreProvider. ' +
            'Make sure to wrap your component tree with <GlobalStoreProvider>.');
    }
    return store;
}
